import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code, BarChart3, Workflow, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHeader from '../components/PageHeader';
import { services, pipeline } from '../mock';

const iconMap = { Code, BarChart3, Workflow };

export default function ServicesPage() {
  return (
    <>
      <PageHeader
        index="// 01"
        eyebrow="Services"
        title="Everything you need to build,"
        accent="measure, and automate."
        intro="Custom web development, analytics, and automation — delivered by a team that ships."
      />

      <section className="relative py-20 bg-surface overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bloom rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon] || Code;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 2) * 0.08, duration: 0.6 }}
                  className="group rounded-2xl border border-line bg-raise hover:border-purple-500/40 hover:bg-raise2 transition-all duration-300 p-8"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/20 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="font-mono-ui text-xs text-purple-500/70">0{service.id}</span>
                  </div>

                  <h3 className="font-display text-2xl font-semibold text-strong mb-2">{service.title}</h3>
                  <p className="text-soft leading-relaxed mb-6">{service.description}</p>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-soft">
                        <Check className="w-4 h-4 text-purple-500 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          {/* Pipeline strip */}
          <div className="mt-20">
            <div className="flex items-center gap-3 mb-8">
              <span className="eyebrow text-purple-500">// flow</span>
              <span className="eyebrow">Our delivery pipeline</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-4 font-display text-xl sm:text-2xl">
              {pipeline.map((p, i) => (
                <React.Fragment key={p.step}>
                  <span className={i % 2 === 0 ? 'text-purple-500 font-semibold' : 'text-pink-500 font-semibold'}>
                    {p.label}
                  </span>
                  {i < pipeline.length - 1 && <ArrowRight className="w-5 h-5 text-faint" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30 group"
              >
                Discuss your project
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
